import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Trophy,
  Award,
  GraduationCap,
  ListChecks,
  Sparkles,
  Activity,
  Target,
  ArrowRight,
} from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const PURPOSE_ICONS = [Sparkles, Activity, Target, ListChecks];

const CoachD = () => {
  const { t } = useTranslation();
  const purpose = t("coach.purpose", { returnObjects: true });
  const methodOutcomes = t("coach.method_outcomes", { returnObjects: true });
  const sessionSteps = t("coach.session_steps", { returnObjects: true });
  const background = t("coach.background", { returnObjects: true });
  const trophies = t("coach.trophies", { returnObjects: true });
  const certs = t("coach.certs", { returnObjects: true });

  return (
    <section
      id="coach"
      data-testid="coach-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        {/* Title — full width, on a single line */}
        <div className="max-w-[1100px]">
          <span className="eyebrow">{t("coach.eyebrow")}</span>
          <h2 className="font-display mt-5 text-xl sm:text-[1.45rem] lg:text-[1.75rem] font-semibold leading-[1.18] text-white glow-text-white uppercase tracking-[-0.005em]">
            {t("coach.title")}
          </h2>
        </div>

        {/* Lead + Purpose mini-list — sit side by side on lg, stacked on mobile */}
        <div className="mt-7 grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
              {t("coach.lead")}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="glass rounded-2xl p-7 electric-border">
              <span className="eyebrow">{t("coach.purpose_title")}</span>
              <ul className="mt-5 space-y-3">
                {Array.isArray(purpose) &&
                  purpose.map((p, i) => {
                    const Icon = PURPOSE_ICONS[i % PURPOSE_ICONS.length];
                    return (
                      <li
                        key={i}
                        data-testid={`coach-purpose-${i}`}
                        className="flex items-start gap-3 text-sm text-white/85"
                      >
                        <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF3DCB]" />
                        <span>{p}</span>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </div>

        {/* Method block */}
        <div className="mt-20 grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <span className="eyebrow">{t("coach.method_title")}</span>
            <p className="mt-5 text-base md:text-lg text-white/75 leading-relaxed">
              {t("coach.method_lead")}
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {Array.isArray(methodOutcomes) &&
                methodOutcomes.map((m, i) => (
                  <div
                    key={i}
                    data-testid={`coach-outcome-${i}`}
                    className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/85 hover:border-[#3DBFFF]/40 transition-colors"
                  >
                    {m}
                  </div>
                ))}
            </div>
          </div>

          {/* Session timeline */}
          <div className="lg:col-span-6">
            <span className="eyebrow">{t("coach.session_title")}</span>
            <p className="mt-4 text-sm text-white/70">{t("coach.session_lead")}</p>
            <ol className="mt-6 space-y-3">
              {Array.isArray(sessionSteps) &&
                sessionSteps.map((s, i) => (
                  <motion.li
                    key={i}
                    data-testid={`coach-session-${i}`}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="relative flex gap-5 rounded-xl glass p-5 electric-border"
                  >
                    <span className="font-display number-badge text-3xl font-bold leading-none">
                      0{i + 1}
                    </span>
                    <div>
                      <h4 className="font-display text-lg font-semibold text-white">
                        {s.h}
                      </h4>
                      <p className="mt-1 text-sm text-white/65 leading-relaxed">
                        {s.p}
                      </p>
                    </div>
                  </motion.li>
                ))}
            </ol>
          </div>
        </div>

        {/* Background strip */}
        <div className="mt-20 grid gap-5 md:grid-cols-3">
          {Array.isArray(background) &&
            background.map((b, i) => (
              <motion.div
                key={i}
                data-testid={`coach-background-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass rounded-2xl p-7 electric-border"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#6FF6FF]">
                  {t("coach.background_label")} · 0{i + 1}
                </span>
                <h4 className="font-display mt-3 text-xl font-semibold text-white">
                  {b.h}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-white/65">{b.p}</p>
              </motion.div>
            ))}
        </div>

        {/* Trophies + Certs */}
        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="glass-strong rounded-2xl p-8 electric-border">
              <div className="flex items-center gap-3">
                <Trophy className="h-5 w-5 text-[#FF3DCB]" />
                <span className="eyebrow">{t("coach.trophies_title")}</span>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {Array.isArray(trophies) &&
                  trophies.map((tr, i) => (
                    <li
                      key={i}
                      data-testid={`coach-trophy-${i}`}
                      className="flex items-start gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3 text-sm text-white/90"
                    >
                      <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF2D5C]" />
                      <span>{tr}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass-strong rounded-2xl p-8 electric-border">
              <div className="flex items-center gap-3">
                <GraduationCap className="h-5 w-5 text-[#3DBFFF]" />
                <span className="eyebrow">{t("coach.certs_title")}</span>
              </div>
              <ul className="mt-6 space-y-3">
                {Array.isArray(certs) &&
                  certs.map((c, i) => (
                    <li
                      key={i}
                      data-testid={`coach-cert-${i}`}
                      className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3 text-sm text-white/90"
                    >
                      <Award className="h-4 w-4 flex-shrink-0 text-[#9B4DFF]" />
                      <span>{c}</span>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoachD;
